import { formatearFechaHora, formatearRevision, NOMBRE_ETAPA } from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { queryClient, useTRPC } from "../api.ts";
import { Barras, Logo } from "../componentes/Barras.tsx";
import { Lectores } from "../componentes/Lectores.tsx";
import { abreviarNombre, mensajeDe } from "../utiles.ts";

/**
 * Pantalla 04 · Acuse de lectura. La única pensada primero para el celular: sin sidebar, una
 * columna, y las dos acciones fijas abajo. Es adonde lleva el aviso de publicación.
 */
export function PaginaAcuse() {
  const { codigo } = useParams({ from: "/movil/acuse/$codigo" });
  const trpc = useTRPC();
  const detalle = useQuery(trpc.acuses.detalle.queryOptions({ codigo }));
  const dar = useMutation(
    trpc.acuses.dar.mutationOptions({
      // El acuse mueve el "N de M" de la lista, la bandeja "Por leer" y el contador del menú.
      onSuccess: () => queryClient.invalidateQueries(),
    }),
  );

  const d = detalle.data;
  const rev = d ? `Rev. ${formatearRevision(d.revision.numero)}` : "";

  return (
    <div className="mx-auto flex min-h-screen max-w-[520px] flex-col bg-fondo">
      <header className="flex items-center gap-3 bg-marino-profundo px-4 py-3 text-white">
        <Logo variante="blanco" alto={24} />
        <span className="text-rojo-marca">
          <Barras alto={9} />
        </span>
        <Link to="/lecturas" className="ml-auto text-[12px] font-semibold text-sidebar-texto">
          Por leer
        </Link>
      </header>

      {detalle.isPending && <p className="p-5 text-texto-3">Cargando…</p>}
      {detalle.error && <p className="p-5 text-rojo">{mensajeDe(detalle.error)}</p>}

      {d && (
        <>
          <main className="flex-1 space-y-3 px-4 pt-4 pb-40">
            <div>
              <div className="font-display text-[11px] font-semibold tracking-[.12em] text-texto-3 uppercase">
                Documento actualizado · {d.documento.proceso.nombre}
              </div>
              <div className="font-mono text-[15px] font-semibold text-marino">
                {d.documento.codigo} · {rev}
              </div>
            </div>

            <section className="rounded-tarjeta border border-borde bg-white p-4">
              <h1 className="text-[17px] leading-snug font-bold text-marino">
                {d.documento.nombre}
              </h1>
              <p className="mt-1 text-[12.5px] text-texto-3">
                {d.revision.aprobadaEn &&
                  `Aprobado el ${formatearFechaHora(d.revision.aprobadaEn)}`}
                {d.firmas.find((f) => f.etapa === "aprobo")?.nombre &&
                  ` por ${d.firmas.find((f) => f.etapa === "aprobo")?.nombre}`}
                .
                {d.revision.reemplazaA !== null &&
                  ` Reemplaza a la Rev. ${formatearRevision(d.revision.reemplazaA)}.`}
              </p>
              {d.revision.motivo && (
                <div className="mt-3">
                  <div className="mb-1 text-[11px] font-semibold tracking-[.08em] text-texto-3 uppercase">
                    Qué cambió
                  </div>
                  <p className="border-l-[3px] border-marino pl-3 text-[13.5px] whitespace-pre-line text-texto-2">
                    {d.revision.motivo}
                  </p>
                </div>
              )}
            </section>

            <section className="rounded-tarjeta border border-borde bg-white p-4">
              <div className="mb-2 text-[11px] font-semibold tracking-[.08em] text-texto-3 uppercase">
                Firmas
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
                {d.firmas.map((f) => (
                  <span key={f.etapa}>
                    <span className="text-texto-3">{NOMBRE_ETAPA[f.etapa]} </span>
                    <span className="font-semibold">
                      {f.nombre ? abreviarNombre(f.nombre) : "—"}
                    </span>
                  </span>
                ))}
              </div>
            </section>

            {d.lectura.total > 0 && (
              <section className="rounded-tarjeta border border-borde bg-white p-4">
                <div className="mb-2 text-[11px] font-semibold tracking-[.08em] text-texto-3 uppercase">
                  Acuses del proceso
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13.5px]">
                    <strong>
                      {d.lectura.leidos} de {d.lectura.total}
                    </strong>{" "}
                    {d.lectura.total === 1 ? "persona ya lo leyó" : "personas ya lo leyeron"}
                  </span>
                  <Lectores lectura={d.lectura} maximo={5} conTexto={false} />
                </div>
              </section>
            )}
          </main>

          <footer className="fixed inset-x-0 bottom-0 mx-auto max-w-[520px] space-y-2 border-t border-borde bg-white px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)]">
            {dar.error && <p className="text-[12.5px] text-rojo">{mensajeDe(dar.error)}</p>}
            {d.revision.pdfId ? (
              <a
                href={`/archivos/${d.revision.pdfId}?ver=1`}
                target="_blank"
                rel="noreferrer"
                className="block rounded-md border border-borde bg-white py-3 text-center text-[14px] font-semibold text-marino"
              >
                Abrir el PDF
              </a>
            ) : (
              <p className="text-center text-[12.5px] text-texto-3">
                El PDF todavía se está preparando.
              </p>
            )}
            {d.miAcuse.estado === "pendiente" && (
              <button
                type="button"
                disabled={dar.isPending}
                onClick={() => dar.mutate({ revisionId: d.revision.id })}
                className="block w-full rounded-md bg-marino py-3.5 text-center text-[15px] font-semibold text-white disabled:opacity-60"
              >
                {dar.isPending ? "Guardando…" : `Leí y entiendo la ${rev}`}
              </button>
            )}
            {d.miAcuse.estado === "leido" && (
              <div className="rounded-md bg-verde-fondo py-3 text-center text-[13.5px] font-semibold text-verde">
                Diste acuse el {formatearFechaHora(d.miAcuse.leidoEn)}
              </div>
            )}
            {d.miAcuse.estado === "no_aplica" && (
              <p className="text-center text-[12.5px] text-texto-3">
                Este documento no te pide acuse de lectura.
              </p>
            )}
          </footer>
        </>
      )}
    </div>
  );
}
